import { ComponentType, FC, PropsWithChildren, useEffect } from 'react';
import { connect } from 'react-redux';

import { getSectionRefActions } from 'actions';
import { MAX_PAGE_SIZE } from 'api';
import { IRootState } from 'reducers';
import { hasContextUserInfoErrorSelector } from 'selectors';
import { shouldLoadCache } from 'utils';

interface IReferenceDataProps {
  state: IRootState;
  linkMode: boolean | undefined;
  hasContextUserInfoError: boolean;
  getSectionRef: typeof getSectionRefActions.request;
}

/**
 * Компонент загружает справочные данные приложения исходя из полученного контекста пользователя
 * и признака доступа к портфолио по ссылке
 * upd: в SP-6630 было принято решение вынести вызов справочников в компоненты, в которых они используются
 * sectionRef было решено оставить здесь, т.к. он используется в большом количестве компонентов
 */
const ReferenceData: FC<IReferenceDataProps & PropsWithChildren> = ({
  children,
  state,
  linkMode,
  hasContextUserInfoError,
  getSectionRef,
}) => {
  const load = (key: keyof IRootState) => shouldLoadCache(state, key);

  useEffect(() => {
    if (hasContextUserInfoError) return;
    if (linkMode === undefined) return;

    const queryParams = { size: MAX_PAGE_SIZE };

    // справочники только для просмотра НЕ по ссылке
    if (!linkMode) {
      load('sectionRef') && getSectionRef(queryParams);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkMode]);

  return <>{children}</>;
};

const ReferenceDataRedux = connect(
  (state: IRootState) => ({
    state,
    linkMode: state.linkMode.mode,
    hasContextUserInfoError: hasContextUserInfoErrorSelector(state),
  }),
  {
    getSectionRef: getSectionRefActions.request,
  },
)(ReferenceData);

const withReferenceData = () => {
  return <P extends PropsWithChildren<unknown>>(WrappedComponent: ComponentType<P>): FC<P> => {
    return function WithReferenceData({ ...props }) {
      return (
        <ReferenceDataRedux>
          <WrappedComponent {...props} />
        </ReferenceDataRedux>
      );
    };
  };
};

export default withReferenceData;
